import React, { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import analyticsTrack from '@lib/analytics/analyticsTrack';
import useGetStartedCtaLink from '@hooks/useGetStartedCtaLink/useGetStartedCtaLink';
import useHolidaySale from '@hooks/useHolidaySale/useHolidaySale';
import trackHomeGetStarted from '@components/pages/Home/lib/trackHomeGetStarted';
import {
  PACKAGE_DETAILS,
  PACKAGE_NAMES,
  PRICES,
  salePackageDiscount,
} from '@components/shared/PrincingCardsV2/common';
import HomeDesignPackageCard from './HomeDesignPackageCard/HomeDesignPackageCard';
import styles from './DesignPackages.module.scss';

interface IDesignPackagesProps {
  eyebrow?: string;
  title?: React.ReactNode;
  analyticsEventName?: string;
  getStartedCtaSection?: string;
}

const DesignPackages = ({
  eyebrow = 'Our design services',
  title = (
    <>
      Real designers, real expertise&mdash;
      <br />
      two ways to collaborate
    </>
  ),
  analyticsEventName = 'home_design_packages_cta_click',
  getStartedCtaSection,
}: IDesignPackagesProps = {}) => {
  const router = useRouter();
  const onlineLink = useGetStartedCtaLink();
  const holidaySale = useHolidaySale();

  const sectionRef = useRef<HTMLElement>(null);
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && !hasTrackedRef.current) {
          hasTrackedRef.current = true;
          observer.disconnect();
          analyticsTrack('package_cards_viewed');
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const fullDiscount = salePackageDiscount(PACKAGE_NAMES.FULL, holidaySale);
  const atHomeDiscount = salePackageDiscount(PACKAGE_NAMES.AT_HOME, holidaySale);

  const fullSalePrice = fullDiscount?.salePrice ?? PRICES.full.sale;
  const atHomeSalePrice = atHomeDiscount?.salePrice ?? PRICES['at home'].sale;

  return (
    <section
      id="packages-section"
      ref={sectionRef}
      className={styles.Root}
      data-test="design-packages"
    >
      <div className={styles.Container}>
        <p className={styles.Eyebrow}>{eyebrow}</p>
        <h2 className={styles.Title}>{title}</h2>

        <div className={styles.Cards}>
          <HomeDesignPackageCard
            dataTest="design-packages-virtual"
            title={(
              <>
                Virtual
                <br />
                Interior Design
              </>
            )}
            eyebrowPill="with a professional designer"
            description="Work one-on-one with a designer, fully online. They handle the plan, the product picks, and the layout — you handle the timing."
            variant="terracotta"
            cornerBadge={fullDiscount ? {
              label: fullDiscount.discountText,
              variant: 'terracotta',
            } : undefined}
            price={(
              <>
                <span className={styles.PriceTopLabel}>&nbsp;</span>
                <div className={styles.PriceRow}>
                  <span className={styles.PriceStrike}>${PRICES.full.msrp}</span>
                  <span className={styles.PriceSale}>${fullSalePrice}</span>
                </div>
                <span className={styles.PriceUnit}>per room</span>
              </>
            )}
            ctaLabel="Get started online"
            ctaOnClick={(e) => {
              analyticsTrack(analyticsEventName, { card: 'virtual' });
              if (getStartedCtaSection) {
                trackHomeGetStarted(e.currentTarget, getStartedCtaSection, 'Get started online');
              }
              router.push(onlineLink);
            }}
          />

          <HomeDesignPackageCard
            dataTest="design-packages-at-home"
            title={(
              <>
                In-Person
                <br />
                Interior Design
              </>
            )}
            eyebrowPill="with an in-person designer"
            description="White-glove expert design for when the project needs someone in the room. A designer comes to your home, builds the plan with you, and sees it through."
            variant="terracotta"
            cornerBadge={atHomeDiscount ? {
              label: atHomeDiscount.discountText,
              variant: 'terracotta',
            } : undefined}
            price={(
              <>
                <span className={styles.PriceTopLabel}>First room at</span>
                <div className={styles.PriceRow}>
                  <span className={styles.PriceStrike}>${PRICES['at home'].msrp}</span>
                  <span className={styles.PriceSale}>${atHomeSalePrice}</span>
                </div>
                <span className={styles.PriceUnit}>
                  plus ${PRICES['at home'].addon} for each additional room
                </span>
              </>
            )}
            ctaLabel="Book a consultation"
            ctaOnClick={() => {
              analyticsTrack(analyticsEventName, { card: 'at_home' });
              router.push('/in-person');
            }}
            locations={PACKAGE_DETAILS[PACKAGE_NAMES.AT_HOME].locations}
          />

          {/* AI Design card hidden temporarily — will be re-enabled later.
          <HomeDesignPackageCard
            dataTest="design-packages-ai"
            title={(
              <>
                AI
                <br />
                Design
              </>
            )}
            showSparkleIcon
            eyebrowPill="with designer insights"
            description={'Free and fast intelligence trained by real designers. Try a layout, '
              + 'swap a sofa, see your living room and bedroom redesigned in seconds — then shop '
              + 'the look directly.'}
            variant="navy"
            cornerBadge={{ label: 'Beta', variant: 'lavender' }}
            price={(
              <>
                <span className={styles.PriceTopLabel}>&nbsp;</span>
                <div className={styles.PriceRow}>
                  <span className={styles.PriceSaleNavy}>Free</span>
                </div>
                <span className={styles.PriceUnit}>
                  Get designer help when needed,
                  <br />
                  additional charges apply
                </span>
              </>
            )}
            ctaLabel="Start free"
            ctaHref={chatUrl}
            ctaTarget="_blank"
            ctaOnClick={() => {
              analyticsTrack(analyticsEventName, { card: 'ai' });
            }}
          />
          */}
        </div>
      </div>
    </section>
  );
};

export default DesignPackages;
